import Typography from "@mui/material/Typography";
import React, {useContext, useRef, useState} from "react";
import Loading from "../../../components/Loading";
import {Stack} from "@mui/material";
import Button from "@mui/material/Button";
import DinoDialog from "../../../components/Dialog";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import DisabledWrapper from "../../../components/DisabledWrapper";
import GeneralStepper from "../../../components/GeneralStepper";
import ProviderData from "./ProviderData";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Box from "@mui/material/Box";
import {hasPermission} from "../../../utils/permissionUtils";
import {AuthContext} from "../../../context/AuthContext";
import BillingoDisplay from "./BillingoDisplay";
import SzamlazzDisplay from "./SzamlazzDisplay";
import BillingoDetails from "./BillingoDetails";
import SzamlazzDetails from "./SzamlazzDetails";
import SubscriptionContext from "../../../context/SubscriptionContext";
import useBillingcredentialQueries from "../../../hooks/queries/useBillingCredentials";


const Billing = () => {
    const [current, setCurrent] = useState(-1)
    const [newOpen, setNewOpen] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const dataRef = useRef(null)
    const editRef = useRef(null)
    const {getUser} = useContext(AuthContext)
    const {hasPlan} = useContext(SubscriptionContext);
    const user = getUser()

    const {
        billingcredentialListQuery,
        useCreateBillingcredential,
        useUpdateBillingcredential,
        useDeleteBillingcredential,
        useBillingcredentialMakeActive,
        useBillingcredentialDeactivate
    } = useBillingcredentialQueries()

    const {data: billingCredentials, isPending: billingCredentialsLoading} = billingcredentialListQuery
    const create = useCreateBillingcredential({})
    const update = useUpdateBillingcredential({})
    const destroy = useDeleteBillingcredential({})
    const activate = useBillingcredentialMakeActive({})
    const deactivate =  useBillingcredentialDeactivate({})

    const [data, setData] = useState({
        billing_service: 'billingo'
    })

    const findBillingCredential = id => billingCredentials.find(p => p.id === id)
    const createBillingCredential = data => create.mutate(data)
    const editBillingCredential = (id, data) => update.mutate({id, data})
    const deleteBillingCredential = id => destroy.mutate(id)
    const makeActive = id => activate.mutate(id)
    const makeInactive = id => deactivate.mutate(id)

    const DISPLAY_MAP = {
        billingo: <BillingoDisplay/>,
        szamlazz: <SzamlazzDisplay/>
    }

    const DATA_MAP = {
        billingo: <BillingoDetails/>,
        szamlazz: <SzamlazzDetails/>
    }

    const validateSelectProvider = () => {
        return {
            valid: !!data?.billing_service,
            error: 'A továbblépéshez szükséges egy szolgáltatót választani!'
        }
    }

    const validateData = () => {
        const data = dataRef.current.getData()
        return {
            valid: !!data,
            error: 'A továbblépéshez minden adatot meg kell adni!'
        }
    }

    const disabledCause = [{
        disabled: !hasPlan('billing', 'create'),
        message: 'A jelenlegi csomagod nem tartalmazza ezt a funkciót. Válassz egy nagyobb csomagot a használathoz!'
    }, {
        disabled: !hasPermission(user, 'billing', 'create'),
        message: 'Nincs jogosultságod ehhez a művelethez!'
    }]

    if (billingCredentialsLoading || create.isPending || update.isPending || destroy.isPending || activate.isPending || deactivate.isPending) return <Loading isLoading />

    return <div className='white-card'>
        <Typography variant='subtitle1'>Számlázási adatok módosítása</Typography>
        <Typography variant='body2' color='#536B74'>
            Adj hozzá vagy módosítsd a számlázási beállításaidat!
        </Typography>
        <Stack spacing={2} marginBottom={2} marginTop={2}>
            {billingCredentials?.map(cred => <Box key={`billing-credential-${cred?.id}`}>{
                React.cloneElement(DISPLAY_MAP[cred?.billing_service] || <div/>, {
                    credential: cred,
                    onMakeActive: id => makeActive(id),
                    onDeactivate: id => makeInactive(id),
                    onEdit: id => {
                        setCurrent(id)
                        setEditOpen(true)
                    },
                    onDelete: id => {
                        setCurrent(id)
                        setDeleteOpen(true)
                    }
                })
            }</Box>)}
        </Stack>
        <DisabledWrapper disabledCause={disabledCause}>
            <Button
                variant='contained'
                onClick={() => setNewOpen(true)}
                endIcon={<AddCircleRoundedIcon/>}
            >
                Új hozzáadása
            </Button>
        </DisabledWrapper>

        <DinoDialog
            open={deleteOpen}
            title='Számlázási mód törlése'
            subtitle='Biztos vagy benne hogy szeretnéd törölni a számlázási módot?'
            handleClose={() => setDeleteOpen(false)}
            actions={<Stack direction='row' justifyContent='end' spacing={1} marginTop={3}>
                <Button variant='outlined' onClick={() => setDeleteOpen(false)}>Mégsem</Button>
                <Button variant='contained' onClick={() => {
                    setDeleteOpen(false)
                    deleteBillingCredential(current)
                }} endIcon={<DeleteRoundedIcon/>}>Törlés</Button>
            </Stack>}
        />


        <DinoDialog
            open={newOpen}
            title='Új számlázási mód hozzáadása'
            subtitle='Válassz madj írd be a számlázási mód nevét és adatait'
            handleClose={() => setNewOpen(false)}
        >
            <GeneralStepper steps={[{
                label: 'Szolgáltató kiválasztása:',
                component: <ProviderData data={data} setData={setData}/>,
                validate: validateSelectProvider
            }, {
                label: 'Adatok megadása',
                component: React.cloneElement(DATA_MAP[data?.billing_service] || <div/>, {reference: dataRef}),
                validate: validateData
            }]} onFinish={() => {
                setNewOpen(false)
                const credData = dataRef.current.getData();
                createBillingCredential({...credData, ...data})
            }}/>
        </DinoDialog>

        <DinoDialog
            open={editOpen}
            title='Számlázási mód szerkesztése'
            subtitle='Írd át a számlázási mód nevét és adatait'
            handleClose={() => setEditOpen(false)}
            actions={<Stack direction='row' justifyContent='end' spacing={1} marginTop={3}>
                <Button variant='outlined' onClick={() => setEditOpen(false)}>Mégsem</Button>
                <Button variant='contained' onClick={() => {
                    const data = editRef.current.getData()
                    if (data) {
                        editBillingCredential(current, data)
                        setEditOpen(false)
                    }
                }} endIcon={<EditRoundedIcon/>}>Szerkesztés</Button>
            </Stack>}
        >
            {React.cloneElement(DATA_MAP[findBillingCredential(current)?.billing_service] || <div/>, {
                reference: editRef,
                obj: findBillingCredential(current)
            })}
        </DinoDialog>
    </div>
}

export default Billing